import { useCallback, useMemo } from 'react';
import { TableFetchParams, TableState, tableQueryMaker } from '@core/table';
import { useService } from '@core/inversify-react';
import { GetBoxTransfersRepoType, IGetBoxTransfersRepo } from '../../repos';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import { BoxTransfersRequestType } from '../../interfaces';
import { boxTransfersQueryKeys } from '@modules/box-transfers/utils';

export const useBoxTransfersTableContext = (requestId: string | number = '', requestType: BoxTransfersRequestType = BoxTransfersRequestType.BRANCH) => {
  const getBoxTransfersRepo = useService<IGetBoxTransfersRepo>(GetBoxTransfersRepoType);
  const { id = requestId, type = requestType } = useParams<{ id: string; type: BoxTransfersRequestType }>();

  const handleFetch = useCallback(
    async (params: TableFetchParams) => {
      const result = await getBoxTransfersRepo.execute(tableQueryMaker(params), type);

      if (result.status === 200) {
        return {
          data: result.response.data,
          total: result.response.total,
        };
      } else {
        message.error(result.response);
        throw new Error(result.response);
      }
    },
    [getBoxTransfersRepo, type],
  );

  const filterId = useMemo(() => {
    switch (type) {
      case BoxTransfersRequestType.DECLARATION:
        return boxTransfersQueryKeys.declaration.id;
      case BoxTransfersRequestType.CONTAINER:
        return boxTransfersQueryKeys.container.id;
      default:
        return boxTransfersQueryKeys.branch.id;
    }
  }, [type]);

  const hiddenFilterId = useMemo(() => {
    switch (type) {
      case BoxTransfersRequestType.DECLARATION:
        return boxTransfersQueryKeys.declaration.trackCode;
      default:
        return '';
    }
  }, [type]);

  const defaultState = useMemo<Partial<TableState>>(
    () => ({
      filters: [
        {
          id: filterId,
          value: id ? Number(id) : undefined,
        },
      ],
      hiddenColumns: [hiddenFilterId],
    }),
    [id, filterId, hiddenFilterId],
  );

  return { handleFetch, defaultState };
};
